import { difference, isNil, omit, pick } from 'lodash';
import { Stop } from '@commutifi/modes';
import { LEG_TYPES } from '@commutifi-fe/constants';
import { AddressPoint, AddressPointWithTimezone, RouteLeg, CommuteTransport, Frequency } from 'shared/interfaces';
import { CommuteProfile, DaysValueEnum, LegType } from 'shared/interfaces/Commute';
import { DEFAULT_WEEKLY_FREQUENCY } from 'constants/onboarding';
import { logger } from '@commutifi-fe/logger';
import { sortDays } from './moment';
import { getArrayValue } from '.';

/**
 * Initially we have some stops:    [\{S\}, \{T\}]
 * End result should be:            [\{S\}, \{S -\> T\}, \{T\}]
 *
 * 1. Keep first stop:              [\{S\}]
 * 2. Start at second stop and use
 *    previous stop to add new leg: Previous \{S\} + Current \{T\} -\> [\{S\}, \{S -\> T\}, \{T\}]
 * 3. Repeat step 2 for remaining stops
 *
 * @param stops - Activities the user stops to do
 */
const _insertLegsBetweenStops = (stops: RouteLeg[] | undefined, transport: CommuteTransport | undefined) => {
  if (!stops || stops.length < 1) {
    logger.warning('You are trying to link array of stops of length 0');
    return stops;
  }

  // --- STEP 1 ---
  const legs = [stops[0]];
  for (let i = 1; i < stops.length; ++i) {
    // --- STEP 2 ---
    const previousStop = stops[i - 1];
    const currentStop = stops[i];
    legs.push({
      type: LegType.Leg,
      startLocation: previousStop.endLocation,
      startAddress: previousStop.endAddress,
      endLocation: currentStop.startLocation,
      endAddress: currentStop.startAddress,
      category: transport?.category,
      ...(transport?.mode && {
        mode: transport.mode
      })
    });
    legs.push(currentStop);
  }
  // --- STEP 3 ---
  // (for loop)
  return legs;
};

/**
 * Input values:    srcLegs -\> [\{A -\> B\}, \{B -\> C\}], stops -\> [\{S\}, \{T\}], atIndex - 1
 * Expected output: [\{A -\> B\}, \{B -\> S\}, \{S\}, \{S -\> T\}, \{T\}, \{T -\> C\}]
 *
 * Steps to achieve that:
 * 1. Extract targeted leg (\{B -\> C\})
 *
 * 2. Prepare this leg by splitting it into 2 to be able
 *    to insert the stops in between. One leg need to finish by the first activity
 *    and the second one by the last activity
 *    Result: \{B -\> S\} and \{T -\> C\}
 *
 * 3. Generate legs to link the different stops
 *    Result: [\{S\}, \{S -\> T\}, \{T\}]
 *
 * 4. Insert the linked stops between the 2 legs generated on step 2
 *
 * @param srcLegs -legs on which to insert one or multiple stops
 * @param stops - stop(s) to insert into the source legs at index specified in params
 * @param atIndex - index at which you want to insert the stops
 */
export const insertStopsAt = (srcLegs: RouteLeg[], stops: RouteLeg[], atIndex: number) => {
  if (stops.length < 1) {
    logger.warning('You are trying to insert stops of length 0 into a commute route');
    return srcLegs;
  }

  // --- STEP 1 ---
  // If the user chose a route autocompleted with google we want to invalidate it since
  // it doesn't make sense anymore. Thus the use of omit transit
  const targetedLeg = omit(srcLegs[atIndex], 'transit');
  const legsWithStops = [...srcLegs];

  // --- STEP 2 ---
  // Alter copied source legs to replace {B -\> C} by 2 legs
  legsWithStops.splice(atIndex, 1, {
    ...targetedLeg,
    endLocation: stops[0].startLocation,
    endAddress: stops[0].startAddress
  }, {
    ...targetedLeg,
    startLocation: stops[stops.length - 1].endLocation,
    startAddress: stops[stops.length - 1].endAddress
  });

  // --- STEP 3 ---
  const linkedStops = _insertLegsBetweenStops(stops, pick(targetedLeg, ['category', 'mode']));

  // --- STEP 4 ---
  // We use atIndex + 1 because we want to insert the stops
  // after the generated {B -\> S} leg
  legsWithStops.splice(atIndex + 1, 0, ...(linkedStops || []));
  return legsWithStops;
};

/**
 * Remove legs that are between stops to remove the bridge we made between those stops.
 * That allows to remove the stops without having extra legs.
 *
 * To proceed we will check if the next leg is a stop and if there is another leg after
 * that stop. If it is the case we will remove those 2 legs
 *
 * @example Processing \{A -\> Stop -\> B -\> Stop2 -\> C\} (A will be the original legs vs. A')
 * Start: First leg shouldn't be a Stop -\> \{A -\> Stop\}
 * 1. A is followed by a stop and another leg -\> \{A' -\> B\}
 * 2. First conditions not filled, and we ignore it cause it is a stop
 * 3. B is meets the conditions so we modify the end address of the new legs -\> \{A' -\> C\}
 * 4. Idem than step 2, ignore stop...
 * 5. C is the last leg so it won't meet the first condition. after than we need to check
 *    if it connects with our last leg in our new set of legs. If yes, we keep it. Otherwise,
 *    it means the last new leg is already pointing to the end address of the original leg C
 * Final: \{A' -\> C\}
 *
 * @param legs - Legs of a route
 */
export const removeStops = (legs: RouteLeg[] | undefined) => {
  if (!legs || legs.length === 0) return [];
  return legs.reduce((filteredLegs, leg, index) => {
    // When the previous leg is not a stop we can already insert it without making any
    // modifications
    if (legs[index - 1] && legs[index - 1].type === LEG_TYPES.leg && leg.type === LEG_TYPES.leg) {
      filteredLegs.push(leg);
    }

    // When the next leg is a stop and it exists another leg after that one, we need
    // to modify the previous leg retained to point to the leg after the stop
    if (legs[index + 2] && legs[index + 1].type === LEG_TYPES.stop) {
      filteredLegs[filteredLegs.length - 1] = {
        ...filteredLegs[filteredLegs.length - 1],
        endLocation: legs[index + 2].endLocation,
        endAddress: legs[index + 2].endAddress
      };
      return filteredLegs;
    }
    return filteredLegs;
  }, [legs[0]]);
};
export const generateStopFromLocation = (address: AddressPoint, activity = Stop.DropOffKids): RouteLeg => ({
  type: LegType.Stop,
  activity,
  category: null,
  startAddress: address.formattedAddress,
  endAddress: address.formattedAddress,
  startLocation: address.location,
  endLocation: address.location
});

/**
 * Return the part before the ',' in a google formatted address
 * @param address - Google formatted address which display the route number
 *                  and street name before the comma
 */
export const extractStreetFromGoogleAddress = (address = '') => address.split(',')[0];
export const generateRouteFromMode = (startPoint: AddressPoint, endPoint: AddressPoint | AddressPointWithTimezone, transport: CommuteTransport): CommuteProfile => {
  const routeGeometry = {
    startAddress: startPoint.formattedAddress,
    endAddress: endPoint.formattedAddress,
    startLocation: startPoint.location,
    endLocation: endPoint.location
  };
  return {
    ...routeGeometry,
    legs: [{
      category: transport.category,
      mode: transport.mode,
      type: LegType.Leg,
      ...routeGeometry
    }]
  };
};
type OptionalFrequency = Frequency | undefined;
const differenceBetweenDaysFrequency = (workFrequency: OptionalFrequency, comparedFrequency: OptionalFrequency | null): Frequency => {
  const weeklyFrequency = (workFrequency?.weeklyFrequency || DEFAULT_WEEKLY_FREQUENCY) - (comparedFrequency?.weeklyFrequency || 0);
  const daysDifference = workFrequency?.days ? difference(workFrequency.days, comparedFrequency?.days || []) : undefined;
  return {
    days: daysDifference,
    weeklyFrequency: daysDifference?.length || weeklyFrequency
  };
};
/**
 * This function will be very useful to calculate the remaining available days and weekly frequency
 * of a question that depends on other frequency questions. Usually the first param will be the work frequency
 * as it is our basis to calculate other frequencies, but it is not a must.
 *
 * The work frequency will default to 5 days a week if it is not set when we need that value to calculate the available
 * frequency.
 *
 * @param workFrequency -           [[Frequency]] object representing the user commute profile frequency
 * @param comparedFrequencies -     Single (object) or multiple (array) [[Frequency]] to remove from the
 *                                 source frequency a.k.a. workFrequency. They can be undefined as they will be filtered and not considered
 * @param weeklyFrequencyReserved - number of days to reserve for inferred values like the primary route frequency
 *                                 (Primary = Work - Remote - Secondary = we need to keep an extra day for the primary route to exist)
 */
export const calculateAvailableFrequency = (workFrequency: OptionalFrequency, comparedFrequencies: OptionalFrequency | OptionalFrequency[], weeklyFrequencyReserved = 0) => {
  const validFrequenciesToCompare = getArrayValue<OptionalFrequency>(comparedFrequencies).filter(freq => !isNil(freq)) as Frequency[];
  const fallbackFrequency = {
    days: workFrequency?.days,
    weeklyFrequency: workFrequency?.weeklyFrequency || DEFAULT_WEEKLY_FREQUENCY
  };
  const frequencyDiff: Frequency = validFrequenciesToCompare.length > 0 ? validFrequenciesToCompare.reduce((frequency: Frequency, comparedFrequency: Frequency | undefined) => differenceBetweenDaysFrequency(frequency, comparedFrequency), fallbackFrequency) : fallbackFrequency;

  // The user cannot take his secondary commute more often that:
  // work frequency - remote frequency - 1 (keep at least one day for the primary route)
  // If the work frequency is not defined yet, we assume 5 days a week
  return {
    ...frequencyDiff,
    weeklyFrequency: Math.max(frequencyDiff.weeklyFrequency - weeklyFrequencyReserved, 0)
  };
};
export const combineFrequencies = (frequencies: (OptionalFrequency | null)[]) => frequencies.reduce((acc: Frequency, frequency) => {
  if (!frequency) {
    return acc;
  }
  const combinedFrequency = {
    ...acc
  };
  if (frequency.weeklyFrequency) {
    combinedFrequency.weeklyFrequency += frequency.weeklyFrequency;
  }
  if (frequency.days) {
    combinedFrequency.days = sortDays([...(combinedFrequency.days || []), ...frequency.days] as DaysValueEnum[]);
  }
  return combinedFrequency;
}, {
  weeklyFrequency: 0,
  days: null
});